import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { SelectOption } from '@/shared/ui/filters';
import { useProjectSiteParams, useUrlState } from '@/shared/lib/hooks';
import { useDebounce } from '@/shared/lib/hooks/use-debounce';

import { getTransportModeOptions } from '@/features/dashboard-filters';
import { useGetOrganizationsQuery } from '@/entities/organizations';
import { useGetDashboardOptionsQuery } from '@/entities/dashboard';

import { DEFAULT_MAPPING_URL_STATE } from '../model/url-state';

export function useMappingFilters() {
  const { t } = useTranslation();

  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_MAPPING_URL_STATE);
  const { projectId, siteId } = useProjectSiteParams();

  const selectedVehicleCategories = useMemo(() => {
    return urlState.vehicleCategories ? urlState.vehicleCategories.split(',').filter(Boolean) : [];
  }, [urlState.vehicleCategories]);

  const [organizationSearch, setOrganizationSearch] = useState('');

  const debouncedOrganizationSearch = useDebounce(organizationSearch, 300);

  const { data: optionsData } = useGetDashboardOptionsQuery({ projectId, siteId });

  const { data: organizationsData, isLoading: isLoadingOrganizations } = useGetOrganizationsQuery({
    search: debouncedOrganizationSearch || undefined,
    projectId
  });

  const organizationOptions: SelectOption[] = useMemo(() => {
    if (!organizationsData?.organizations) return [];

    return organizationsData.organizations.map((org) => ({
      value: org.id.toString(),
      label: org.name,
      keywords: org.abbreviation ? [org.abbreviation] : undefined
    }));
  }, [organizationsData]);

  const vehicleCategoryOptions: SelectOption[] = useMemo(() => {
    const vehicleCategories = optionsData?.vehicleCategories || [];
    // Remove the 'None' option (first item) as it's not needed for the filter
    const options = getTransportModeOptions(t, vehicleCategories);
    return options.filter((opt) => opt.value !== 'NO');
  }, [t, optionsData?.vehicleCategories]);

  const filterValues = useMemo(
    () => ({
      consignmentNumber: urlState.consignmentNumber || undefined,
      organizationId: urlState.organizationId || undefined,
      // Include vehicle categories if they have values
      ...(selectedVehicleCategories.length > 0 ? { vehicleCategories: selectedVehicleCategories } : {})
    }),
    [urlState.consignmentNumber, urlState.organizationId, selectedVehicleCategories]
  );

  const hasActiveFilters = useMemo(() => {
    return !!(urlState.consignmentNumber || urlState.organizationId || selectedVehicleCategories.length > 0);
  }, [urlState.consignmentNumber, urlState.organizationId, selectedVehicleCategories]);

  const setConsignmentNumber = useCallback(
    (value: string) => {
      setUrlState({ consignmentNumber: value });
    },
    [setUrlState]
  );

  const setSelectedOrganization = useCallback(
    (value?: string) => {
      setUrlState({ organizationId: value || '' });
    },
    [setUrlState]
  );

  const setSelectedVehicleCategories = useCallback(
    (values: string[]) => {
      setUrlState({ vehicleCategories: values.join(',') });
    },
    [setUrlState]
  );

  const resetFilters = useCallback(() => {
    resetUrlState();
    setOrganizationSearch('');
  }, [resetUrlState]);

  return {
    // Filter values
    consignmentNumber: urlState.consignmentNumber,
    selectedOrganization: urlState.organizationId,
    selectedVehicleCategories,

    // Filter setters
    setConsignmentNumber,
    setSelectedOrganization,
    setSelectedVehicleCategories,

    // Organization search
    organizationSearch,
    setOrganizationSearch,
    isLoadingOrganizations,

    // Options for dropdowns
    organizationOptions,
    vehicleCategoryOptions,

    // Computed values
    filterValues,
    hasActiveFilters,

    // Actions
    resetFilters
  };
}
